import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { ProductCollectionResponse } from "@hermes/api-model-catalog";
import { ModelObject } from "@hermes/api-model-core";
import { LOCALE, Settings } from "@hermes/app-core";
import { Locale } from "@hermes/locale";

import { getUrlFormattedPrices } from "../../helpers/criterias-helper/criterias.helper";
import { gridDataFromProductCollectionResponse } from "../../mappers/grid.mapper";
import {
    DEFAULT_SORT,
    FH_SORT_BY_REGEX,
    GridData,
    Sort,
    PriceRange,
    Facet,
} from "../../model/grid.model";

export interface HttpParametersProductsService {
    [key: string]: string;
}

export interface MerchandisingCriterias {
    pimCode?: string;
    searchTerm?: string;
    urlParams: string;
    sort?: Sort;
    prices?: PriceRange;
    offset: number;
    pageSize: number;
    display?: boolean;
}

/**
 * Merchandising service call the FredHopper rest API
 */
@Injectable()
export class MerchandisingService {
    private locale = inject(LOCALE);
    private http = inject(HttpClient);
    private settings = inject(Settings);

    /**
     * Fetch grid datas from criterias
     */
    public fetchGridDataFromCriterias(
        criterias: MerchandisingCriterias,
        isCombinedFacetActivated: boolean,
        facet?: Facet,
    ): Observable<GridData> {
        const params = this.handleParameters(
            criterias,
            isCombinedFacetActivated,
            facet,
        );
        return this.callHttpProductsService(params);
    }

    /**
     * This method will return http params based on given criteria
     */
    public handleParameters(
        criterias: MerchandisingCriterias,
        isCombinedFacetActivated: boolean,
        facet?: Facet,
    ): HttpParametersProductsService {
        const params: HttpParametersProductsService = {
            /**
             * merch encoded raw url params
             *
             * Prevent fetch error :
             * - Currently some fh_location settings are separated by ";"
             * which creates a problem in the apiGateway and in the backend.
             * - Remove fh_sort_by=... from url params to avoid fetch error
             */
            urlParams: criterias.urlParams
                ? criterias.urlParams
                      .replace(/;/g, "%3B")
                      .replace(FH_SORT_BY_REGEX, "")
                : "",
            // get local from context as `fr_fr`
            locale: this.locale.code,
            // get pim code from criteria as `WOMEN` if defined
            category: criterias.pimCode || "",
            // get search term
            searchterm: criterias.searchTerm ?? "",
            // get sort from criteria or add default sort if not defined
            sort: criterias.sort || DEFAULT_SORT,
            // get prices if defined as formatted url price
            // eslint-disable-next-line @typescript-eslint/naming-convention
            fh_range_price:
                criterias.prices === undefined
                    ? ""
                    : getUrlFormattedPrices(criterias.prices),
            // get offset if defined
            offset:
                criterias.offset && criterias.offset > 0
                    ? `${criterias.offset}`
                    : "",
            // finally get pagesize if defined
            pagesize: criterias.pageSize ? `${criterias.pageSize}` : "",
        };

        if (isCombinedFacetActivated && facet?.enabled) {
            // If facet checked, add it to param array with facet name, eg: facet_category and his value
            facet?.combinedFacets.forEach((facetItem) => {
                params[facetItem.facetName] = facetItem.facetNameValue;
            });
        } else if (facet?.enabled) {
            params[facet.facetName] = facet.facetNameValue;
        }
        return params;
    }

    /**
     * Call backend service '/products' to retrieve results search data or grid data
     */
    private callHttpProductsService(
        params: HttpParametersProductsService,
    ): Observable<GridData> {
        let httpParameters = new HttpParams();
        // Remove undefined or "" or null values and append the parametters
        Object.entries(params)
            .filter(([, parameterValue]) => parameterValue)
            .forEach(
                ([parameterName, parameterValue]) =>
                    (httpParameters = httpParameters.append(
                        parameterName,
                        parameterValue,
                    )),
            );
        return this.http
            .get(`${this.settings.apiUrl}/products`, {
                params: httpParameters,
            })
            .pipe(
                // mapping from Model Object into Grid Interfaces is made here because we want to simulate that is corresponding to the response of the backend API
                map((result) =>
                    gridDataFromProductCollectionResponse(
                        ModelObject.fromJsonData(
                            result,
                            ProductCollectionResponse,
                        ),
                    ),
                ),
            );
    }
}
